import { CloseOutlined } from "@ant-design/icons";
import { Drawer, Tag } from "antd";
import dayjs from "dayjs";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { DATE_FORMAT, RELATIONSHIP } from "utils/constants";
import AlertDetailView from "./AlertDetailView";
import AlertsTable from "./AlertsTable";
import { usePPEGEL, VIEWS } from "./PPEGELProvider";
import PersonSelector from "./PersonSelector";
import { isNamedHouseholdMember } from "./utils";

const getInitials = (fullName) => {
  if (!fullName) return "";
  const parts = fullName.trim().split(/\s+/);
  return parts
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("");
};

const FieldGroup = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="ppegel-profile__field">
      <span className="ppegel-profile__field-label">{label}</span>
      <span className="ppegel-profile__field-value">{value}</span>
    </div>
  );
};

const PersonProfile = ({ person, t }) => {
  const kayros = person?.kayrosData || {};
  const FIELDS = t("DETAIL.FIELDS", { returnObjects: true });

  return (
    <div className="ppegel-profile">
      <div className="ppegel-profile__avatar-section">
        <div className="avatar--text avatar--large avatar--bg--primary">
          {getInitials(person.fullName)}
        </div>
        <h3 className="ppegel-profile__name">{person.fullName}</h3>
        <Tag className="ppegel-profile__role-badge">
          {t(`ROLES.${person.role}`) || person.role}
        </Tag>
      </div>
      <div className="ppegel-profile__divider" />
      <div className="ppegel-profile__fields">
        <FieldGroup label={FIELDS.CIVILITY} value={kayros.civilite} />
        <FieldGroup label={FIELDS.LAST_NAME} value={kayros.nom} />
        <FieldGroup label={FIELDS.FIRST_NAME} value={kayros.prenom} />
        <FieldGroup
          label={FIELDS.BIRTH_DATE}
          value={
            kayros.birthDate
              ? dayjs(kayros.birthDate).format(DATE_FORMAT)
              : null
          }
        />
        <FieldGroup label={FIELDS.EMAIL} value={kayros.email} />
        {get(kayros, "relatedPersons", [])
          .filter(isNamedHouseholdMember)
          .map((relatedPerson) => (
            <FieldGroup
              key={`relatedPersonId-${get(relatedPerson, "id")}`}
              label={
                get(relatedPerson, "relationship") === RELATIONSHIP.SPOUSE
                  ? FIELDS.SPOUSE
                  : FIELDS.CHILDREN
              }
              value={`${get(
                relatedPerson,
                "identity.user.firstname",
              )} ${get(relatedPerson, "identity.user.lastname")}`}
            />
          ))}
      </div>
    </div>
  );
};

const PPEGELDrawer = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PPEGEL);
  const { drawerOpen, closeDrawer, view, persons, selectedPersonIndex } =
    usePPEGEL();

  const selectedPerson = persons[selectedPersonIndex];
  const isDetailView = view === VIEWS.DETAIL;

  return (
    <Drawer
      placement="right"
      width={920}
      open={drawerOpen}
      onClose={closeDrawer}
      destroyOnClose={false}
      className="ppegel-drawer"
      closable={false}
      footer={null}
    >
      <div className="ppegel-drawer__top-bar">
        <button
          type="button"
          className="ppegel-drawer__close-btn"
          onClick={closeDrawer}
        >
          {t("CLOSE")} <CloseOutlined />
        </button>
      </div>
      <div className="ppegel-drawer__layout">
        <div className="ppegel-drawer__sidebar">
          {isDetailView && selectedPerson ? (
            <PersonProfile person={selectedPerson} t={t} />
          ) : (
            <PersonSelector />
          )}
        </div>
        <div className="ppegel-drawer__content">
          {!isDetailView && selectedPerson && (
            <div className="ppegel-drawer__person-header">
              <h3 className="ppegel-drawer__person-name">
                {selectedPerson.fullName}
              </h3>
              <span className="ppegel-drawer__person-role">
                {t(`ROLES.${selectedPerson.role}`) || selectedPerson.role}
              </span>
            </div>
          )}
          {isDetailView ? <AlertDetailView /> : <AlertsTable />}
        </div>
      </div>
    </Drawer>
  );
};

export default PPEGELDrawer;
